import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatFilesPopover } from "@/components/chat/ChatFilesPopover";
import { ChatTranscriptSearchBar } from "@/components/chat/ChatTranscriptSearchBar";
import { ContextMenu } from "@/components/common/ContextMenu";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { collectChatFiles } from "@/lib/chat-files";
import { projectMessages } from "@/lib/chat-render";
import { chatTranscriptText } from "./chat-transcript-text";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";
import { useOrgUsersStore } from "@/stores/useOrgUsersStore";
import { formatFullName } from "@/lib/format-name";
import { TOP_GAP } from "@/lib/pane-styles";

/** 1 行目の高さ。pane-header の既定高から上下パディングを引いた値で、タイトルの位置を 1 行構成のときと揃える。 */
const TITLE_ROW_HEIGHT = 32;

/** ヘッダ中身の上端余白。pane-header 既定の 8px より 5px 詰めて、2 行全体を上に寄せる。 */
const CONTENT_TOP_PADDING = 3;

/**
 * 履歴検索欄・ファイル一覧の上端。1 行目 (ボタンの行) の真下に少し空けて出す。
 * 1 行目は overflow:hidden なのでその中に置くと切られてしまうため、ヘッダ直下の子として
 * 絶対配置し、位置を 1 行目の寸法から組み立てる。
 */
const POPUP_TOP = TOP_GAP + CONTENT_TOP_PADDING + TITLE_ROW_HEIGHT + 4;

/** ヘッダ直下に出すポップアップの配置。右端はボタン列 (= ヘッダ内容の右端) に揃える。 */
const POPUP_STYLE = {
	position: "absolute",
	top: POPUP_TOP,
	right: "var(--pane-header-padding-x)",
} as const;

interface ChatHeaderProps {
	/** タイトル未確定の新規チャット下書きでは null。 */
	chatTitle: string | null;
	/** 履歴検索を閉じる。閉じたあとのフォーカス復帰は CenterPane が担う。 */
	onCloseTranscriptSearch: () => void;
}

async function copyTranscript() {
	const { messages } = useChatStore.getState();
	const blocks = projectMessages(messages, { pendingUserMessageId: null, waitingToolUseId: null });
	try {
		await navigator.clipboard.writeText(chatTranscriptText(blocks));
	} catch (err: unknown) {
		useToastStore
			.getState()
			.show({ type: "error", message: "チャット履歴をコピーできませんでした" });
		throw err;
	}
	useToastStore.getState().show({ type: "success", message: "チャット履歴をコピーしました" });
}

function ChatHeaderMenu() {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	return (
		<>
			<IconButton
				ref={buttonRef}
				icon="kebab"
				iconSize={15}
				onClick={() => setOpen((v) => !v)}
				data-testid="chat-header-menu"
			/>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current }}
					onClose={() => setOpen(false)}
					testId="chat-header-menu-popup"
					items={[
						{
							label: "チャット履歴をコピー",
							onSelect: () => void copyTranscript(),
							testId: "transcript-copy",
						},
					]}
				/>
			)}
		</>
	);
}

export function ChatHeader({ chatTitle, onCloseTranscriptSearch }: ChatHeaderProps) {
	const chatCreatedById = useChatStore((s) => s.chatCreatedById);
	const chatArchived = useChatStore((s) => s.chatArchived);
	const environmentName = useChatStore((s) => s.environmentName);
	const environmentRunnerName = useChatStore((s) => s.environmentRunnerName);
	const projectName = useChatStore((s) => s.projectName);
	const chatId = useChatStore((s) => s.chatId);
	const environmentId = useChatStore((s) => s.environmentId);
	// 履歴が無ければ検索・ファイル一覧の対象も無いため、ボタン自体を出さない。
	const messages = useChatStore((s) => s.messages);
	const hasMessages = messages.length > 0;
	const openTranscriptSearch = useTranscriptSearchStore((s) => s.openSearch);
	const transcriptSearchOpen = useTranscriptSearchStore((s) => s.open);
	const files = useMemo(() => collectChatFiles(messages), [messages]);
	const [filesOpen, setFilesOpen] = useState(false);
	const filesButtonRef = useRef<HTMLButtonElement>(null);
	const closeFiles = useCallback(() => setFilesOpen(false), []);
	// 検索欄とファイル一覧は同じ場所に出すため、同時には開かない。
	const toggleFiles = useCallback(() => {
		useTranscriptSearchStore.getState().closeSearch();
		setFilesOpen((open) => !open);
	}, []);
	useEffect(() => {
		if (transcriptSearchOpen) setFilesOpen(false);
	}, [transcriptSearchOpen]);
	// 確定済みチャットで環境 ID が無い = 環境が削除された孤児チャット。
	const environmentDeleted = chatId !== null && environmentId === null;
	const creator = useOrgUsersStore((s) =>
		chatCreatedById ? s.usersById[chatCreatedById] : undefined,
	);
	const creatorName = creator ? formatFullName(creator) : null;
	const titleTooltip = [projectName, chatTitle].filter(Boolean).join("/");
	return (
		<header
			data-testid="session-header"
			className="pane-header"
			style={{
				flexDirection: "column",
				alignItems: "stretch",
				justifyContent: "flex-start",
				gap: 0,
				paddingTop: TOP_GAP + CONTENT_TOP_PADDING,
				background: "var(--surface-0)",
				borderBottom: "none",
				boxShadow: "var(--shadow-header-boundary)",
				position: "relative",
				zIndex: 1,
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					minHeight: TITLE_ROW_HEIGHT,
					minWidth: 0,
					overflow: "hidden",
				}}
			>
				<h1
					title={titleTooltip}
					data-tooltip-if-truncated
					style={{
						display: "flex",
						alignItems: "baseline",
						gap: 4,
						margin: 0,
						minWidth: 0,
						maxWidth: 420,
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						whiteSpace: "nowrap",
					}}
				>
					{projectName && (
						<>
							{/* 幅が足りないときは、チャット名より先にプロジェクト名を縮める。 */}
							<span
								data-testid="session-header-project"
								style={{
									flexShrink: 3,
									minWidth: 0,
									color: "var(--fg-3)",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: 160,
								}}
							>
								{projectName}
							</span>
							{chatTitle && <span style={{ flexShrink: 0, color: "var(--fg-3)" }}>/</span>}
						</>
					)}
					{chatTitle && (
						<span
							data-testid="session-header-title"
							style={{
								minWidth: 0,
								color: "var(--fg-1)",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{chatTitle}
						</span>
					)}
				</h1>
				{chatArchived && (
					<span
						data-testid="session-header-archived-badge"
						style={{
							flexShrink: 0,
							fontSize: "var(--text-section)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-heading)",
							color: "var(--fg-3)",
							background: "var(--surface-1)",
							borderRadius: "var(--radius-2)",
							padding: "2px 6px",
							whiteSpace: "nowrap",
						}}
					>
						アーカイブ済み
					</span>
				)}
				{hasMessages && (
					<div style={{ display: "flex", gap: 4, marginLeft: "auto", flexShrink: 0 }}>
						<IconButton
							ref={filesButtonRef}
							icon="file"
							iconSize={15}
							title={`チャットに読み込まれたファイル (${files.length})`}
							onClick={toggleFiles}
							data-testid="chat-files-open"
						/>
						<IconButton
							icon="search"
							iconSize={15}
							title="チャット履歴を検索 (Ctrl+F)"
							onClick={openTranscriptSearch}
							data-testid="transcript-search-open"
						/>
						<ChatHeaderMenu />
					</div>
				)}
			</div>
			{transcriptSearchOpen && (
				<div style={POPUP_STYLE}>
					<ChatTranscriptSearchBar onClose={onCloseTranscriptSearch} />
				</div>
			)}
			{filesOpen && chatId !== null && (
				<div style={POPUP_STYLE}>
					<ChatFilesPopover
						chatId={chatId}
						files={files}
						anchorRef={filesButtonRef}
						onClose={closeFiles}
					/>
				</div>
			)}
			{(creatorName || environmentName || environmentDeleted) && (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						minWidth: 0,
						overflow: "hidden",
					}}
				>
					{creatorName && (
						<span
							data-testid="session-header-creator"
							title={`作成者:${creatorName}`}
							data-tooltip-if-truncated
							style={{
								flexShrink: 3,
								minWidth: 0,
								overflow: "hidden",
								textOverflow: "ellipsis",
								fontSize: "var(--text-caption)",
								color: "var(--fg-2)",
								whiteSpace: "nowrap",
							}}
						>
							<span style={{ color: "var(--fg-3)", marginRight: 4 }}>作成者:</span>
							{creatorName}
						</span>
					)}
					{environmentDeleted && (
						<span
							data-testid="session-header-environment-deleted"
							style={{
								flexShrink: 0,
								fontSize: "var(--text-caption)",
								color: "var(--fg-3)",
								whiteSpace: "nowrap",
							}}
						>
							(削除されたエージェント)
						</span>
					)}
					{environmentName && (
						<span
							data-testid="session-header-environment"
							title={
								environmentRunnerName
									? `${environmentName} (デバイス: ${environmentRunnerName})`
									: environmentName
							}
							data-tooltip-if-truncated={environmentRunnerName ? undefined : true}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								flexShrink: 2,
								minWidth: 0,
								fontSize: "var(--text-caption)",
								color: "var(--fg-2)",
							}}
						>
							{/* デバイスの環境はローカルと分かるアイコン (カードの「デバイス: 名前」のバッジと同じ意味)、クラウドは雲。 */}
							{environmentRunnerName ? (
								<span
									data-testid="session-header-environment-runner"
									style={{ display: "inline-flex", flexShrink: 0, color: "var(--fg-3)" }}
								>
									<Icon name="monitor" size={15} stroke={1.1} />
								</span>
							) : (
								<svg
									width={15}
									height={15}
									viewBox="0 0 16 16"
									fill="none"
									style={{ flexShrink: 0, color: "var(--fg-3)" }}
								>
									<path
										d="M4.5 12.5h6.75a2.75 2.75 0 0 0 .45-5.46 3.75 3.75 0 0 0-7.16-.86A2.75 2.75 0 0 0 4.5 12.5z"
										stroke="currentColor"
										strokeWidth={1.1}
										strokeLinejoin="round"
										fill="none"
									/>
								</svg>
							)}
							<span
								style={{
									minWidth: 0,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									maxWidth: 200,
								}}
							>
								{environmentName}
							</span>
						</span>
					)}
				</div>
			)}
		</header>
	);
}
